import { addMessage, addMessageAndSetRendered, handleApiError } from './messagesActions';
import request from 'superagent';

// LOGIN FORM
export var loginSubmit = (data) => {
  return (dispatch) => {
    return request
      .post('/web_portal/api/v1/login')
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .send({
        login: data.email,
        password: data.password,
        recaptchaToken: data.recaptchaToken,
      })
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then((res) => {
        const out = JSON.parse(res.text);

        // Watch for 2FA confirmation code being required for this user after they enter their email/password
        if (out.code_2fa_required === true) {
          return out;
        }

        dispatch(setUserDetailsAndTiles(out.userDetails, out.tiles));
        dispatch(addMessage(out.message, 'success'));
        return out;
      })
      .catch((err) => {
        dispatch(handleApiError(err, false));
        throw err;
      });
  };
};

const TWO_FA_INVALIDATED = 'Too many failed attempts. Your 2FA code has been invalidated. Please log in again to receive a new code.';

const TWO_FA_SESSION_EXPIRED = 'No active 2FA session found. Please log in again.';

const twoFaLoginRedirect = (err) => {
  if (err?.twoFaInvalidated || err?.twoFaSessionExpired) {
    return err;
  }
  const text = typeof err?.response?.text === 'string' ? err.response.text : '';
  if (text.includes(TWO_FA_INVALIDATED)) {
    return Object.assign(err, { twoFaInvalidated: true, flashMessage: TWO_FA_INVALIDATED });
  }
  if (text.includes(TWO_FA_SESSION_EXPIRED)) {
    return Object.assign(err, { twoFaSessionExpired: true, flashMessage: TWO_FA_SESSION_EXPIRED });
  }
  return null;
};

// LOGIN CODE VERIFICATION FORM
export const loginCodeVerificationSubmit = (data) => {
  return (dispatch) => {
    return request
      .post('/web_portal/api/v1/2fa_login')
      .type('form')
      .send({
        confirmation_code: data.confirmationCode,
        // Email was spec'd to find the record to check. But it is not used, odoo instead implemented a new session var for this purpose.
        email: data.email,
      })
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then((res) => {
        if (res.status >= 400) {
          const err = { response: res };
          const redirectErr = twoFaLoginRedirect(err);
          if (redirectErr) {
            return redirectErr;
          }
          dispatch(handleApiError(err, false));
          throw err;
        }

        const out = JSON.parse(res.text);

        // On success, we should receive user details and log the user in
        if (out.userDetails && out.tiles) {
          dispatch(setUserDetailsAndTiles(out.userDetails, out.tiles));
        }

        dispatch(addMessage(out.message, 'success'));
        return out;
      })
      .catch((err) => {
        if (err?.twoFaInvalidated || err?.twoFaSessionExpired) {
          return err;
        }
        const redirectErr = twoFaLoginRedirect(err);
        if (redirectErr) {
          return redirectErr;
        }
        dispatch(handleApiError(err, false));
        throw err;
      });
  };
};

// Set the user's latest details. sessionId is an auth flag only (real Odoo id stays in PHP session).
export const setUserDetailsAndTiles = (details, tiles) => {
  return (dispatch, getState) => {
    const tilesContent = getState().config.content?.tiles || {};
    dispatch({
      type: 'SET_USER_DETAILS',
      details,
      tiles: Array.isArray(tiles)
        ? tiles.map((tile) => ({
            ...tile,
            title: tilesContent[tile.type]?.title || tile.name,
          }))
        : tiles,
      sessionId: details ? true : null,
    });
  };
};

// Fetch an updated copy of the user's details and set it as our new details.
export const getUserDetails = (sessionId = null) => {
  return (dispatch) => {
    // Don't refresh user details if we're not logged in.
    // The login or validate process will handle that.
    if (!sessionId) {
      return;
    }
    // Fetch the updated user's details from the server.
    request
      .get('/web_portal/api/v1/user')
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // Success
        (res) => {
          try {
            const out = JSON.parse(res.text);
            const { userDetails, tiles } = out;
            dispatch(setUserDetailsAndTiles(userDetails, tiles));
            dispatch(addMessage(out.message, 'success'));
          } catch (err) {
            dispatch(handleApiError(err));
          }
        },
        // Error
        (err) => {
          dispatch(handleApiError(err));
        },
      )
      .finally(() => {
        dispatch({
          type: 'SESSION_CHECK_END',
        });
      });
  };
};

// Check the user's session is valid.
export const checkSession = (sessionId = null) => {
  return (dispatch) => {
    // Don't refresh user details if we're not logged in.
    // The login or validate process will handle that.
    if (!sessionId) {
      return;
    }
    // Fetch the updated user's details from the server.
    request
      .get('/web_portal/api/v1/session_check')
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // Success
        () => {
          // noop
        },
        // Error - session expired
        (err) => {
          dispatch(handleApiError(err));
        },
      );
  };
};

// Log the user out.
export var logout = () => {
  return (dispatch) => {
    dispatch({
      type: 'LOG_OUT_START',
    });
    // Clear the changed fields that were saved while the user was active.
    dispatch({
      type: 'CLEAR_CHANGED_FIELDS',
    });
    return request
      .get('/web_portal/api/v1/logout')
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        (res) => {
          try {
            var out = JSON.parse(res.text);
            dispatch(addMessage(out.message, 'success'));
          } catch (err) {
            // We don't want to log out on 401 failures, as we're already logged out,
            // so we pass in logoutOn401=false
            dispatch(handleApiError(err, false));
          }
          dispatch({
            type: 'LOG_OUT_END',
          });
        },
        (err) => {
          // We don't want to log out on 401 failures, as we're already logged out,
          // so we pass in logoutOn401=false
          dispatch(handleApiError(err, false));
          dispatch({
            type: 'LOG_OUT_END',
          });
        },
      );
  };
};

// FORGOT PASSWORD FORM
export var forgotPasswordSubmit = (data) => {
  return (dispatch) => {
    dispatch({
      type: 'FORGOT_PASSWORD_START',
    });
    request
      .post('/web_portal/api/v1/resetpassword')
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .send({
        email: data.email,
      })
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          try {
            var out = JSON.parse(res.text);
            dispatch(addMessage(out.message, 'success'));
          } catch (err) {
            dispatch(handleApiError(err));
          }
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err));
        },
      )
      .finally(() => {
        // Always mark as ended
        dispatch({
          type: 'FORGOT_PASSWORD_END',
        });
      });
  };
};

// SET PASSWORD FORM
export const showSetPasswordModal = (showCloseButton = false) => {
  return {
    type: 'SETPASSWORD_MODAL_SHOW',
    showCloseButton,
  };
};

export const hideSetPasswordModal = () => {
  return {
    type: 'SETPASSWORD_MODAL_HIDE',
  };
};

export const setPasswordSubmit = (data) => {
  return (dispatch) => {
    request
      .post('/web_portal/api/v1/setpassword')
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .send({
        currentPassword: data.current_password,
        password: data.reset_password,
      })
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          try {
            var out = JSON.parse(res.text);
            dispatch(addMessageAndSetRendered(out.message, 'success'));
            // Re-fetch the user's details which will hide the modal
            dispatch(getUserDetails(true));
          } catch (err) {
            dispatch(handleApiError(err, 'setpassword:danger'));
          }
          // Turn the force password flag off, which marks it as
          // able to fetch new state from the next call to the 'user' endpoint
          dispatch({
            type: 'UNSET_FORCE_PASSWORD_FLAG',
          });
          dispatch({
            type: 'SETPASSWORD_MODAL_HIDE',
          });
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err, 'setpassword:danger'));
        },
      );
  };
};

// VALIDATE USER
export var validateUser = (token, callback) => {
  return (dispatch) => {
    dispatch({
      type: 'VALIDATE_START',
    });

    request
      .get('/web_portal/api/v1/validate/' + token)
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          dispatch({
            type: 'VALIDATE_END',
          });
          try {
            var out = JSON.parse(res.text);
            // Log the user in using the returned details
            dispatch(setUserDetailsAndTiles(out.userDetails, out.tiles));
            dispatch(addMessage(out.message, 'success'));
          } catch (err) {
            dispatch(handleApiError(err, 'danger', false));
          }
          callback();
        },
        // failure promise
        (err) => {
          dispatch({
            type: 'VALIDATE_END',
          });
          dispatch(handleApiError(err, 'danger', false));
          callback();
        },
      );
  };
};
